import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Card, Modal, Layout, Row, Space, Typography, Col } from 'antd';
import { Button, ButtonGroup } from '@mui/material';
import { useSearchParams, useParams } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import IncomeModelConfigurator from "./IncomeModelConfigurator";
import StockChart from "../../charts/StockChart";
import PnL from "../../Components/PnL";
import { fetchIncomeModel, selectIncomeModels, resetIncomeModels, removeIncomeModel } from "../../Store/slices/timeseriesModels.slice";
import {AddRounded, RestoreRounded, SearchRounded, DeleteForeverRounded} from "@mui/icons-material";
import {stringMixin, money} from "../../utils/strings";
import TickerSearch from "../../Components/TickerSearch/TickerSearch";
import {useEnsureTickersLoaded} from "../../hooks/useEnsureTickersLoaded";


const unixToYYYYMMDD = (unixTimestamp) => {
  const date = new Date(unixTimestamp * 1000); // Convert seconds to milliseconds
  return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};


const formatChartData = (incomeModels) => {
    if (incomeModels.length === 0) return [];

    const portfolioData = [];
    incomeModels.forEach(x=>{
        portfolioData.push({
            name: `Portfolio Series ${incomeModels.indexOf(x+1)}`,
            data: x.portfolio_series.map((point) => [point.timestamp, point.value]),
            type: 'line',
            stroke: x?.color
        })
    });

    const chartData = [
        {
            plot: 0,
            data: portfolioData,
            height: '80%',
            yScale: 'percent',
            legend: {
                format: "%"
            }
        },
        {
            plot: 1,
            data: incomeModels.flatMap((model, index) => [
                {
                    name: `Payment Series ${index + 1}`,
                    data: model.payment_series.map((point) => [point.timestamp, point.amount]).slice(model.ppy || 2),
                    type: 'column',
                    stroke: model?.color,
                    fill: model?.color,
                    thickness: 5,

                }
            ]),
            height: '20%'
        }
    ];
    return chartData;
}



const calculateIncomeStats = (paymentSeries) => {
    const annualPayments = paymentSeries.reduce((acc, payment) => {
        const year = new Date(payment.timestamp).getFullYear();
        if (!acc[year]) acc[year] = 0;
        acc[year] += payment.amount;
        return acc;
    }, {});

    const years = Object.keys(annualPayments).sort();
    const yearlyGrowth = years.slice(1).map((year, index) => {
        const prevYear = years[index];
        const growth = (annualPayments[year] - annualPayments[prevYear]) / annualPayments[prevYear] * 100;
        return { year, growth };
    });

    const averageGrowth = yearlyGrowth.reduce((sum, { growth }) => sum + growth, 0) / yearlyGrowth.length;

    return {
        annualPayments,
        yearlyGrowth,
        averageGrowth
    };
}


const IncomeInstanceMetadata = ({ model })=>{
    const minWithdrawal = model?.summary_stats?.min_withdrawal_amount;
    const withdrawalRate = model?.summary_stats?.withdrawal_rate;
    const initialCapital = model?.summary_stats?.initial_capital;
    const avgPayment = model?.summary_stats?.avg_payment;
    const paymentGrowth = model?.summary_stats?.payment_growth_rate;
    return (
        <Card
            bordered={false}
            className={'gapless p-3'}
            style={{
                borderLeft: `5px solid ${model?.color}`,
                borderRight: '1px solid #ddd',
                borderBottom: '1px solid #ddd',
                borderTop: '1px solid #ddd',
            }}
        >
            <Space direction={'vertical'}>
                <Typography.Text strong>
                    {model?.name}
                </Typography.Text>
                <Space>
                  {stringMixin.pct(withdrawalRate)} | {money(minWithdrawal, 0)}  | {money(initialCapital, 0)}
                </Space>
                <Space>
                    <div>
                        Mean Withdrawal {money(avgPayment, 0)}
                    </div>
                    <div>
                        Growth: <PnL value={paymentGrowth} />
                    </div>
                </Space>
            </Space>

        </Card>
    )
}


const IncomeModel = () => {
    const { portfolioId } = useParams();
    const chartRef = useRef(null);
    const dispatch = useDispatch();
    const incomeModels = useSelector(selectIncomeModels);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const [config, setConfig] = useState({
        initial_capital: 100000,
        ppy: 2,
        withdrawal_rate: 0.03,
        min_withdrawal_amount: 0,
    });

    const portfolio = useSelector((state)=>state.portfolios.items.find(x=>x.id===portfolioId));
    const getStartDate = useCallback(() => {
        return portfolio ? unixToYYYYMMDD(portfolio.inception_date) : undefined;
    }, [portfolio]);

    const _  = useEnsureTickersLoaded(['spy']);


    useEffect(() => {
        if (portfolioId && portfolio && incomeModels.length === 0) {
            fetchModel(portfolioId, portfolio.name);
        }
    }, [portfolioId, portfolio, incomeModels.length]);

    const fetchModel = useCallback(async (tickerId, tickerName) => {
        setLoading(true);
        await dispatch(fetchIncomeModel({
            timeseries_id: tickerId || portfolioId,
            name: tickerName || portfolio?.name,
            startDate: getStartDate(),
            ...config }));
        setLoading(false);
    }, [dispatch, portfolioId, config]);

    const handleResetModels = () => {
        dispatch(resetIncomeModels());
    };

    const callbackOnTicker = (tickers) => {
        const ticker = tickers[0];
        setSelectedId(ticker.symbol);
        setShowSearch(false);
        fetchModel(ticker.symbol, ticker.name);
    };

    const handleRemoveModel = (modelId) => {
        dispatch(removeIncomeModel(modelId));
    };

    const handleConfigChange = (newConfig) => {
        setConfig(newConfig);
    };

    const chartData = formatChartData(incomeModels);

    return (
        <Layout>
            <Layout.Sider width={250} className={'full left white'} style={{padding: '48px 12px'}}>
                <Card bordered={false}
                      title={
                        <ButtonGroup>
                            <Button color={'primary'} variant={'contained'} onClick={()=>setShowSearch(true)}>
                                <SearchRounded/>
                            </Button>
                            <Button onClick={fetchModel} variant={'contained'} color={'info'}><AddRounded/> </Button>
                            <Button onClick={handleResetModels} variant={'outlined'}><DeleteForeverRounded/></Button>
                        </ButtonGroup>
                      }

                >
                    <IncomeModelConfigurator onConfigChange={handleConfigChange} />
                </Card>
            </Layout.Sider>
            <Layout.Content style={{padding: '48px', borderLeft: '1px solid #ddd'}}>

                {incomeModels.length > 0 && (
                    <>
                        <Row gutter={32}>
                            {incomeModels.map((model, index) => (
                                <Col>
                                    <IncomeInstanceMetadata model={model} />
                                </Col>
                            ))}
                        </Row>
                        <Card bordered={false} loading={loading} className={'gapless'}>
                            <div style={{height: 500, width: '100%'}}>
                                <StockChart dataSeries={chartData} ref={chartRef} />
                            </div>
                        </Card>
                    </>
                )}
            </Layout.Content>
            <Modal
                title="Search Ticker"
                open={showSearch}
                onCancel={()=>setShowSearch(false)}
                footer={null}
            >
                <TickerSearch onTickersSubmit={callbackOnTicker} />
            </Modal>
        </Layout>
    );
}

export default IncomeModel;